/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { Button, Dialog, Typography2 } from '@coursera/cds-core';
import { AIGenerateIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import Badge from 'bundles/unified-description-page-common/components/shared/badges/Badge';
import { getAiSkillsModalContent } from 'bundles/unified-description-page-common/utils/getAiSkillsModalContent';
import { getAiSkillsModalSubtitle } from 'bundles/unified-description-page-common/utils/getAiSkillsModalSubtitle';
import { getPartnerRefreshAiBadgeTitle } from 'bundles/unified-description-page-common/utils/getPartnerRefreshAiBadgeTitle';
import { shouldShowNewAiSkillsBadge } from 'bundles/unified-description-page-common/utils/shouldShowNewAiSkillsBadge';

import _t from 'i18n!nls/unified-description-page-common';

const styles = {
  svg: css`
    vertical-align: middle;
    margin-inline-end: var(--cds-spacing-25);
    margin-bottom: 2px;
  `,
  button: css`
    padding: 0;
    vertical-align: baseline;
  `,
  subtitle: css`
    line-height: 1.5;
    margin-bottom: var(--cds-spacing-300);
  `,
  content: css`
    ul {
      padding-left: 1.5em;

      li {
        list-style: disc;
      }
    }
  `,
};

const { HeadingGroup, Content, Actions } = Dialog;

type Props = {
  slug: string | undefined;
  disabled?: boolean;
} & Pick<React.ComponentProps<typeof Badge>, 'designStyle'>;

const NewAiBadge = ({ slug, disabled, designStyle }: Props) => {
  const track = useTracker();
  const [isOpened, setIsOpened] = useState(false);

  const handleClick = () => {
    setIsOpened(true);

    track('click_button', {
      button: {
        name: 'xdp_gen_ai_modal',
      },
    });
  };

  const handleClose = () => {
    setIsOpened(false);
  };

  const shouldRenderNewAiSkillsBadge = shouldShowNewAiSkillsBadge(slug);

  if (!shouldRenderNewAiSkillsBadge) {
    return null;
  }

  return (
    <>
      <Button size="small" variant="ghost" css={styles.button} onClick={handleClick} disabled={disabled}>
        <Badge designStyle={designStyle}>
          <AIGenerateIcon size="small" css={styles.svg} />
          {_t('New AI skills')}
        </Badge>
      </Button>
      <Dialog open={isOpened} onClose={handleClose}>
        <HeadingGroup>{getPartnerRefreshAiBadgeTitle(slug)}</HeadingGroup>
        <Content>
          <Typography2 variant="subtitleMedium" component="p" css={styles.subtitle}>
            {getAiSkillsModalSubtitle(slug)}
          </Typography2>
          <Typography2 variant="bodyPrimary" component="span" css={styles.content}>
            {getAiSkillsModalContent(slug)}
          </Typography2>
        </Content>
        <Actions />
      </Dialog>
    </>
  );
};

export default NewAiBadge;
